import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import TopBar from "@/components/TopBar";
import VerifiedOrgBadge from "@/components/VerifiedOrgBadge";
import { motion } from "framer-motion";
import { Briefcase, Building2, MapPin, Link as LinkIcon, DollarSign, Send, X, Search } from "lucide-react";
import { BRANCH_META } from "@/lib/branches";
import { useAuth } from "@/context/AuthContext";

const ROLE_TYPES = [
    { k: "full_time", label: "Full-time" },
    { k: "part_time", label: "Part-time" },
    { k: "contract", label: "Contract" },
    { k: "internship", label: "Internship" },
];

export default function JobsNew() {
    const { user } = useAuth();
    const nav = useNavigate();
    const [disciplines, setDisciplines] = useState([]);
    const [form, setForm] = useState({
        title: "",
        description: "",
        role_type: "full_time",
        location: "Remote",
        apply_url: "",
        compensation_note: "",
    });
    const [selected, setSelected] = useState([]); // array of discipline slugs
    const [query, setQuery] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        api.get("/atlas/disciplines").then((r) => setDisciplines(r.data)).catch(() => {});
    }, []);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return disciplines.slice(0, 12);
        return disciplines
            .filter(
                (d) =>
                    d.name.toLowerCase().includes(q) ||
                    (d.slug || "").toLowerCase().includes(q) ||
                    (d.branch || "").toLowerCase().includes(q)
            )
            .slice(0, 20);
    }, [disciplines, query]);

    const toggle = (slug) => {
        setSelected((s) => (s.includes(slug) ? s.filter((x) => x !== slug) : s.length >= 5 ? s : [...s, slug]));
    };

    const submit = async (e) => {
        e.preventDefault();
        setError(null);
        if (selected.length === 0) {
            setError("Pick at least one discipline you're recruiting for.");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post("/bounties", {
                title: form.title,
                description: form.description,
                reward_type: "job_opening",
                discipline_slugs: selected,
                role_type: form.role_type,
                location: form.location,
                apply_url: form.apply_url,
                compensation_note: form.compensation_note,
                origin_url: window.location.origin,
            });
            if (data.checkout_url) {
                window.location.href = data.checkout_url;
            } else {
                nav("/careers");
            }
        } catch (err) {
            setError(err.response?.data?.detail || "Could not create listing");
            setBusy(false);
        }
    };

    if (!user) return null;

    return (
        <div className="min-h-screen w-full">
            <TopBar />
            <div className="max-w-3xl mx-auto pt-24 pb-16 px-4 sm:px-8">
                <p className="chip" style={{ background: BRANCH_META.business.soft, color: BRANCH_META.business.text }}>
                    <Briefcase className="w-3 h-3" /> Post a role
                </p>
                <div className="mt-2 flex items-center gap-3">
                    <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-slate-900 leading-none">
                        Recruit from the Atlas.
                    </h1>
                    {user.org_verified && <VerifiedOrgBadge size="lg" />}
                </div>
                <p className="text-slate-600 mt-3">
                    Post a recruitment listing. It runs for 30 days and appears on the Careers
                    page plus on each targeted discipline. Flat <span className="font-mono font-bold text-slate-900">$5</span>{" "}
                    listing fee via Stripe.{" "}
                    {!user.is_organization && (
                        <>
                            You're posting as an individual. To display a "Verified Org" badge,{" "}
                            <a href="/settings" className="text-humanities font-bold">convert your account to an Organization</a>.
                        </>
                    )}
                    {user.is_organization && !user.org_verified && (
                        <>
                            {" "}Your org isn't domain-verified yet — sign up (or reconvert) with an email at your org's domain to earn the badge.
                        </>
                    )}
                </p>

                <motion.form
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    onSubmit={submit}
                    className="crystal rounded-3xl p-6 sm:p-8 mt-6 space-y-5"
                >
                    <Field
                        icon={<Briefcase className="w-4 h-4" />}
                        label="Role title"
                        value={form.title}
                        onChange={(v) => setForm({ ...form, title: v })}
                        testid="job-title"
                        maxLength={140}
                        required
                        placeholder="e.g., Senior ML Engineer"
                    />

                    <label className="block">
                        <div className="text-xs uppercase tracking-widest font-bold text-slate-400 mb-1.5 flex items-center gap-1.5">
                            <Briefcase className="w-4 h-4" /> Role type
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                            {ROLE_TYPES.map((r) => (
                                <button
                                    key={r.k}
                                    type="button"
                                    onClick={() => setForm({ ...form, role_type: r.k })}
                                    data-testid={`job-role-${r.k}`}
                                    className={`chip transition-all ${form.role_type === r.k ? "bg-slate-900 text-cream" : "bg-white/70 text-slate-700 hover:bg-white"}`}
                                >
                                    {r.label}
                                </button>
                            ))}
                        </div>
                    </label>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <Field
                            icon={<MapPin className="w-4 h-4" />}
                            label="Location"
                            value={form.location}
                            onChange={(v) => setForm({ ...form, location: v })}
                            testid="job-location"
                            maxLength={80}
                            placeholder="Remote · New York, NY"
                        />
                        <Field
                            icon={<DollarSign className="w-4 h-4" />}
                            label="Compensation note (optional)"
                            value={form.compensation_note}
                            onChange={(v) => setForm({ ...form, compensation_note: v })}
                            testid="job-comp"
                            maxLength={120}
                            placeholder="$120k–$160k + equity"
                        />
                    </div>

                    <Field
                        icon={<LinkIcon className="w-4 h-4" />}
                        label="Apply URL (external)"
                        value={form.apply_url}
                        onChange={(v) => setForm({ ...form, apply_url: v })}
                        testid="job-apply-url"
                        maxLength={400}
                        placeholder="https://yourco.com/careers/... or mailto:hiring@yourco.com"
                        hint="Where candidates go to apply. Project Atlas hands them off to your flow."
                    />

                    <Field
                        icon={<Building2 className="w-4 h-4" />}
                        label="What you're looking for"
                        value={form.description}
                        onChange={(v) => setForm({ ...form, description: v })}
                        testid="job-description"
                        textarea
                        maxLength={2000}
                        required
                        placeholder="Describe the role, the impact, must-haves, and what a great fit looks like."
                        hint={`${form.description.length}/2000`}
                    />

                    <div>
                        <div className="text-xs uppercase tracking-widest font-bold text-slate-400 mb-1.5 flex items-center gap-1.5">
                            <Briefcase className="w-4 h-4" /> Disciplines you're hiring in (up to 5)
                        </div>
                        {selected.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mb-2" data-testid="job-selected-disciplines">
                                {selected.map((s) => (
                                    <button
                                        type="button"
                                        key={s}
                                        onClick={() => toggle(s)}
                                        className="chip bg-slate-900 text-cream"
                                    >
                                        #{s} <X className="w-3 h-3 ml-0.5" />
                                    </button>
                                ))}
                            </div>
                        )}
                        <div className="flex items-center gap-2 field-input">
                            <Search className="w-4 h-4 text-slate-400" />
                            <input
                                type="text"
                                className="flex-1 bg-transparent outline-none text-sm"
                                placeholder="Search disciplines (e.g., AI, marketing, biology)…"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                data-testid="job-discipline-search"
                            />
                        </div>
                        <div className="flex flex-wrap gap-1.5 mt-2 max-h-52 overflow-y-auto">
                            {filtered.map((d) => (
                                <button
                                    key={d.slug}
                                    type="button"
                                    onClick={() => toggle(d.slug)}
                                    disabled={!selected.includes(d.slug) && selected.length >= 5}
                                    data-testid={`job-discipline-${d.slug}`}
                                    className={`chip transition-all ${
                                        selected.includes(d.slug)
                                            ? "bg-slate-900 text-cream"
                                            : "bg-white/70 text-slate-700 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
                                    }`}
                                    style={
                                        selected.includes(d.slug)
                                            ? {}
                                            : { color: BRANCH_META[d.branch]?.text }
                                    }
                                >
                                    {d.name}
                                </button>
                            ))}
                            {filtered.length === 0 && (
                                <span className="text-xs text-slate-500">No matches for "{query}"</span>
                            )}
                        </div>
                    </div>

                    {error && (
                        <div className="text-sm text-white bg-sciences px-3 py-2 rounded-xl" data-testid="job-error">
                            {error}
                        </div>
                    )}

                    <div className="flex items-center justify-between pt-3 border-t border-white/60">
                        <p className="text-xs text-slate-500 max-w-sm">
                            Continue to Stripe to pay the $5 listing fee. Your role goes live the
                            moment payment succeeds.
                        </p>
                        <button
                            disabled={busy}
                            className="arcade-btn"
                            data-variant="primary"
                            data-testid="job-submit"
                            type="submit"
                        >
                            <Send className="w-4 h-4" /> {busy ? "Redirecting…" : "Continue to $5 checkout"}
                        </button>
                    </div>
                </motion.form>
            </div>
        </div>
    );
}

function Field({ icon, label, value, onChange, testid, hint, textarea, ...rest }) {
    return (
        <label className="block">
            <div className="text-xs uppercase tracking-widest font-bold text-slate-400 mb-1.5 flex items-center gap-1.5">
                {icon} {label}
            </div>
            {textarea ? (
                <textarea
                    className="field-input min-h-[110px] resize-y"
                    value={value ?? ""}
                    onChange={(e) => onChange && onChange(e.target.value)}
                    data-testid={testid}
                    {...rest}
                />
            ) : (
                <input
                    className="field-input"
                    value={value ?? ""}
                    onChange={(e) => onChange && onChange(e.target.value)}
                    data-testid={testid}
                    {...rest}
                />
            )}
            {hint && <p className="text-[11px] text-slate-500 mt-1">{hint}</p>}
        </label>
    );
}
